import { initCommerceApiWithGlobals } from 'common-api';
import { useState, useEffect, useCallback } from 'react';
import type { ProductProps } from 'tsconfig/types';

type ProductCache = {
  [key: string]: ProductProps;
};

const cache: ProductCache = {};
const pendingRequests: Map<string, Promise<ProductProps | null>> = new Map();

const useProductData = (productId?: string) => {
  const productCardAPI = initCommerceApiWithGlobals();
  const [product, setProduct] = useState<ProductProps | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchProductData = useCallback(
    async (id: string) => {
      setLoading(true);

      // Check cache first
      if (cache[id]) {
        setProduct(cache[id]);
        setLoading(false);
        return cache[id];
      }

      // Check for pending request
      if (pendingRequests.has(id)) {
        const existingRequest = pendingRequests.get(id);
        if (existingRequest) {
          const productData = await existingRequest;
          setProduct(productData);
          setLoading(false);
          return cache[id];
        }
      }

      // Make API request
      const request = productCardAPI.Product.getAllProductData(id)
        .then((productData: ProductProps[]) => {
          const firstProduct = productData?.[0] ?? null;
          if (firstProduct) {
            cache[id] = firstProduct;
          }
          setProduct(firstProduct);
          pendingRequests.delete(id);
          return firstProduct;
        })
        .catch(() => {
          pendingRequests.delete(id);
          setProduct(null);
          return null;
        });

      pendingRequests.set(id, request);
      const productData = await request;
      setProduct(productData);
      setLoading(false);

      return productData;
    },
    [productCardAPI],
  );

  // Reset state when productId changes
  useEffect(() => {
    if (productId) {
      setProduct(null);
      setLoading(false);
      fetchProductData(productId);
    }
  }, [productId]);

  return { product, loading, fetchProductData };
};

export default useProductData;
